import { Injectable, WritableSignal, inject, signal } from '@angular/core';

import { REVIEWS_SORT, RatingValues } from '@app/interfaces/reviews.interface';
import { ReviewInfosModel } from '@app/models/review-infos.model';
import { ReviewRepository } from '@app/repositories/review.repository';

import { Observable, Subject, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ReviewsService {
    public readonly DEFAULT_PAGE_SIZE: number = 5;
    public rating$ = new Subject<{ [key: string]: number }>();
    public ratingSignal: WritableSignal<RatingValues | null> = signal({ note: null, count: 0 });

    private reviewRepository: ReviewRepository = inject(ReviewRepository);
    private cachedReviews: { [key: string]: ReviewInfosModel } = {};

    public getAverages(gencodes: string[]) {
        return this.reviewRepository.getProductsAverage(gencodes).pipe(catchError(() => of(null)));
    }

    public getReviews(
        productId: string,
        currentPage: number = 0,
        pageSize: number = this.DEFAULT_PAGE_SIZE,
        sort: REVIEWS_SORT = REVIEWS_SORT.DATE_DESC,
    ): Observable<ReviewInfosModel> {
        const cacheKey = this.getCacheKey(productId, currentPage, sort);
        const cachedReview = this.cachedReviews[cacheKey];
        if (cachedReview) {
            return of(cachedReview);
        }

        const offset = currentPage * pageSize;
        return this.reviewRepository.getProductReview(productId, offset, pageSize).pipe(
            tap((result) => {
                this.cachedReviews[cacheKey] = result;
                this.ratingSignal.set({
                    note: result.note,
                    count: result.reviewCount,
                });
            }),
            catchError(() => of(null)),
        );
    }

    private getCacheKey(gencode: string, currentPage: number, sort: REVIEWS_SORT): string {
        return `cache-${gencode}-${currentPage}-${sort}`;
    }
}
